import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCalendarData,
  type CalendarData,
  type CalendarDay,
} from "../lib/api";
import { adToBS, bsToAD, getCurrentBSDate } from "../lib/dateConverter";
import { calendarDB } from "../lib/indexedDB";

const getKey = (bsYear: number, bsMonth: number) => `bs${bsYear}-${bsMonth}`;

// Helper functions for month toggle logic
const getBSMonthStartADMonth = (bsYear: number, bsMonth: number): { adYear: number; adMonth: number } | null => {
  const adDate = bsToAD(bsYear, bsMonth, 1);
  if (!adDate) return null;
  return { adYear: adDate.ad_y, adMonth: adDate.ad_m };
};

const getADMonthStartBSMonth = (adYear: number, adMonth: number): { bsYear: number; bsMonth: number } | null => {
  const bsDate = adToBS(adYear, adMonth, 1);
  if (!bsDate) return null;
  return { bsYear: bsDate.bs_y, bsMonth: bsDate.bs_m };
};

const getTodayDates = (): { bsDate: string | null; adDate: string | null } => {
  const currentBS = getCurrentBSDate();
  if (!currentBS) return { bsDate: null, adDate: null };
  
  const bsDate = `${currentBS.bs_y}-${String(currentBS.bs_m).padStart(2, "0")}-${String(currentBS.bs_d).padStart(2, "0")}`;
  const adDateObj = bsToAD(currentBS.bs_y, currentBS.bs_m, currentBS.bs_d);
  const adDate = adDateObj 
    ? `${adDateObj.ad_y}-${String(adDateObj.ad_m).padStart(2, "0")}-${String(adDateObj.ad_d).padStart(2, "0")}`
    : null;
  
  return { bsDate, adDate };
};

const isDateInBSMonth = (
  dateKey: string, // BS format: "2082-09-12"
  bsYear: number,
  bsMonth: number,
  calendarData: Record<string, CalendarData>
): boolean => {
  const key = getKey(bsYear, bsMonth);
  const data = calendarData[key];
  if (!data || !data.days) return false;
  return data.days.some(day => day.bs.date === dateKey);
};

const isDateInADMonth = (
  dateKey: string, // AD format: "2025-12-27"
  adYear: number,
  adMonth: number,
  calendarData: Record<string, CalendarData>
): boolean => {
  // Search through all BS months to find if this AD date exists
  for (const key of Object.keys(calendarData)) {
    const data = calendarData[key];
    if (data && data.days) {
      const found = data.days.find(day => day.ad?.date === dateKey);
      if (found && found.ad) {
        return found.ad.ad_y === adYear && found.ad.ad_m === adMonth;
      }
    }
  }
  return false;
};

export interface UseNepaliDatepickerProps {
  year?: number; // AD year (always AD format)
  month?: number; // AD month (always AD format, 1-12)
  calendarMode?: "bs" | "ad";
  value?: string; // Date in format "YYYY-MM-DD" (always AD format)
  onDateSelect?: (date: CalendarDay) => void;
  onMonthChange?: (year: number, month: number) => void;
  onCalendarModeChange?: (mode: "bs" | "ad") => void;
}

export const useNepaliDatepicker = ({
  year: initialYear,
  month: initialMonth,
  calendarMode: initialCalendarMode = "bs",
  value,
  onDateSelect,
  onMonthChange,
  onCalendarModeChange,
}: UseNepaliDatepickerProps) => {
  const getInitialBSYear = () => {
    // Priority 1: Check initialYear/initialMonth props (highest priority)
    if (initialYear && initialMonth) {
      // Convert AD year/month to BS
      const bsDate = adToBS(initialYear, initialMonth, 15); // Use day 15 as anchor
      if (bsDate) {
        return bsDate.bs_y;
      }
    }
    // Priority 2: Check value prop (AD format) - convert to BS
    if (value) {
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const adYear = parseInt(dateParts[0], 10);
        const adMonth = parseInt(dateParts[1], 10);
        const adDay = parseInt(dateParts[2], 10);
        if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
          const bsDate = adToBS(adYear, adMonth, adDay);
          if (bsDate) {
            return bsDate.bs_y;
          }
        }
      }
    }
    // Priority 3: Use current BS date
    const currentBS = getCurrentBSDate();
    return currentBS ? currentBS.bs_y : new Date().getFullYear() + 57;
  };

  const [currentBSYear, setCurrentBSYear] = useState<number>(
    getInitialBSYear()
  );

  const getInitialBSMonth = () => {
    // Priority 1: Check initialYear/initialMonth props (highest priority)
    if (initialYear && initialMonth) {
      // Convert AD year/month to BS
      const bsDate = adToBS(initialYear, initialMonth, 15); // Use day 15 as anchor
      if (bsDate) {
        return bsDate.bs_m;
      }
    }
    // Priority 2: Check value prop (AD format) - convert to BS
    if (value) {
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const adYear = parseInt(dateParts[0], 10);
        const adMonth = parseInt(dateParts[1], 10);
        const adDay = parseInt(dateParts[2], 10);
        if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
          const bsDate = adToBS(adYear, adMonth, adDay);
          if (bsDate) {
            return bsDate.bs_m;
          }
        }
      }
    }
    // Priority 3: Use current BS date
    const currentBS = getCurrentBSDate();
    return currentBS ? currentBS.bs_m : 1;
  };

  const [currentBSMonth, setCurrentBSMonth] = useState<number>(
    getInitialBSMonth()
  );

  const [calendarData, setCalendarData] = useState<
    Record<string, CalendarData>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isYearLoading, setIsYearLoading] = useState<boolean>(false);
  const [isMonthLoading, setIsMonthLoading] = useState<boolean>(false);
  const [isInputLoading, setIsInputLoading] = useState<boolean>(false);
  const [calendarMode, setCalendarMode] = useState<"bs" | "ad">(
    initialCalendarMode
  );
  // Track if user has manually toggled the mode (so props don't override)
  const hasUserToggledMode = useRef<boolean>(false);
  // Track if toggle is in progress to prevent rapid clicks
  const isTogglingRef = useRef<boolean>(false);
  const [isIndexedDBInitialized, setIsIndexedDBInitialized] =
    useState<boolean>(false);

  const [showMonthDropdown, setShowMonthDropdown] = useState<boolean>(false);
  const [showYearDropdown, setShowYearDropdown] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedBSKey, setSelectedBSKey] = useState<string>("");
  const [selectedADKey, setSelectedADKey] = useState<string>("");
  // Track primary AD month/year for AD mode navigation
  const [primaryADMonth, setPrimaryADMonth] = useState<number | null>(null);
  const [primaryADYear, setPrimaryADYear] = useState<number | null>(null);
  // Track original BS month/year when entering AD mode, to restore when toggling back
  const [originalBSMonth, setOriginalBSMonth] = useState<number | null>(null);
  const [originalBSYear, setOriginalBSYear] = useState<number | null>(null);
  
  // Cache the determined AD month for each BS month to ensure consistency across toggles
  // Key: "BS_YEAR-BS_MONTH", Value: { adYear: number, adMonth: number }
  const primaryADMonthForBSMonthRef = useRef<Record<string, { adYear: number; adMonth: number }>>({});

  const titleRef = useRef<HTMLDivElement>(null);

  // Initialize DB
  useEffect(() => {
    const initDB = async () => {
      try {
        await calendarDB.init();
        setIsIndexedDBInitialized(true);
      } catch (error) {
        console.error("Failed to init IndexedDB:", error);
        setIsIndexedDBInitialized(true);
      }
    };
    initDB();
  }, []);

  const fetchMonthData = useCallback(
    async (bsYear: number, bsMonth: number): Promise<CalendarData | null> => {
      try {
        const key = getKey(bsYear, bsMonth);

        // Check cache in state first
        if (calendarData[key]) {
          return calendarData[key];
        }

        // Try IndexedDB
        if (isIndexedDBInitialized) {
          const cached = await calendarDB.get(bsYear, bsMonth);
          if (cached) {
            setCalendarData((prev) => ({ ...prev, [key]: cached }));
            return cached;
          }
        }

        // Fetch from API (API expects BS year/month)
        const data = await fetchCalendarData(bsYear, bsMonth);
        if (data) {
          setCalendarData((prev) => ({ ...prev, [key]: data }));
          if (isIndexedDBInitialized) {
            await calendarDB.set(bsYear, bsMonth, data);
          }
        }
        return data;
      } catch (error) {
        console.error("Error fetching month data:", error);
        return null;
      }
    },
    [isIndexedDBInitialized, calendarData]
  );

  const fetchYearData = useCallback(
    async (bsYear: number) => {
      setIsYearLoading(true);
      try {
        const yearData: Record<string, CalendarData> = {};
        for (let month = 1; month <= 12; month++) {
          const data = await fetchMonthData(bsYear, month);
          if (data) {
            yearData[getKey(bsYear, month)] = data;
          }
        }
        setCalendarData((prev) => ({ ...prev, ...yearData }));
      } catch (err) {
        console.error("Error fetching year data:", err);
      } finally {
        setIsYearLoading(false);
      }
    },
    [fetchMonthData]
  );

  // Helper: find cached or fetch BS month that contains AD month/year
  const findBSMonthForAD = useCallback(
    async (adY: number, adM: number, searchAroundBSYear = currentBSYear) => {
      // Search among cached calendarData first (fast path)
      for (const key of Object.keys(calendarData)) {
        const data = calendarData[key];
        if (!data || !data.days) continue;
        const found = data.days.find(
          (d) => d.ad && d.ad.ad_y === adY && d.ad.ad_m === adM
        );
        if (found) {
          const [, rest] = key.split("bs");
          const [bsYearStr, bsMonthStr] = rest.split("-");
          return {
            bsYear: Number(bsYearStr),
            bsMonth: Number(bsMonthStr),
            data,
          };
        }
      }

      // Not found in cache — search by fetching neighboring BS months
      const maxSteps = 24;
      for (let step = 0; step <= maxSteps; step++) {
        // check forward
        let forwardMonthIndex = currentBSMonth - 1 + step;
        let fYear = currentBSYear + Math.floor(forwardMonthIndex / 12);
        let fMonth = (forwardMonthIndex % 12) + 1;
        const forwardData = await fetchMonthData(fYear, fMonth);
        if (forwardData) {
          const found = forwardData.days.find(
            (d) => d.ad && d.ad.ad_y === adY && d.ad.ad_m === adM
          );
          if (found)
            return { bsYear: fYear, bsMonth: fMonth, data: forwardData };
        }

        if (step === 0) continue;

        // check backward
        let backwardMonthIndex = currentBSMonth - 1 - step;
        let bYear = currentBSYear + Math.floor(backwardMonthIndex / 12);
        while (backwardMonthIndex < 0) {
          backwardMonthIndex += 12;
          bYear -= 1;
        }
        let bMonth = (backwardMonthIndex % 12) + 1;
        const backwardData = await fetchMonthData(bYear, bMonth);
        if (backwardData) {
          const found = backwardData.days.find(
            (d) => d.ad && d.ad.ad_y === adY && d.ad.ad_m === adM
          );
          if (found)
            return { bsYear: bYear, bsMonth: bMonth, data: backwardData };
        }
      }

      return null;
    },
    [calendarData, currentBSMonth, currentBSYear, fetchMonthData]
  );

  // Fetch all BS months that contain days from a target AD month
  const fetchAllBSMonthsForADMonth = useCallback(
    async (adYear: number, adMonth: number): Promise<CalendarDay[]> => {
      const allDays: CalendarDay[] = [];
      const fetchedBSMonths = new Set<string>(); // Track which BS months we've already fetched

      // Validate AD year is within supported range (1921-2032 based on component validation)
      // Note: dateConverter supports up to 2040, but component validation limits to 2032
      if (adYear < 1921 || adYear > 2032) {
        console.warn(`AD year ${adYear} is out of supported range (1921-2032)`);
        return [];
      }

      // Strategy: Find BS months that contain days 1, 15, and last day of the AD month
      // This should cover all BS months that contain any part of the AD month
      const targetDays = [1, 15];
      // Get last day of the AD month
      const lastDayOfMonth = new Date(adYear, adMonth, 0).getDate();
      targetDays.push(lastDayOfMonth);

      for (const adDay of targetDays) {
        try {
          const bsDate = adToBS(adYear, adMonth, adDay);
          if (bsDate) {
            const bsMonthKey = `bs${bsDate.bs_y}-${bsDate.bs_m}`;
            if (!fetchedBSMonths.has(bsMonthKey)) {
              fetchedBSMonths.add(bsMonthKey);
              // Check IndexedDB first, then fetch if needed
              const data = await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
              if (data && data.days) {
                // Filter days that belong to the target AD month
                const daysInTargetMonth = data.days.filter(
                  (day) =>
                    day.ad && day.ad.ad_y === adYear && day.ad.ad_m === adMonth
                );
                allDays.push(...daysInTargetMonth);
              }
            }
          }
        } catch (error) {
          // Skip dates that are out of range or fail conversion
          console.warn(
            `Failed to convert AD date ${adYear}-${adMonth}-${adDay} to BS:`,
            error
          );
          continue;
        }
      }

      // Remove duplicates and sort by AD day
      const uniqueDays = new Map<string, CalendarDay>();
      for (const day of allDays) {
        if (day.ad && day.ad.date) {
          uniqueDays.set(day.ad.date, day);
        }
      }

      return Array.from(uniqueDays.values()).sort((a, b) => {
        if (!a.ad || !b.ad) return 0;
        return a.ad.ad_d - b.ad.ad_d;
      });
    },
    [fetchMonthData]
  );

  // Initial fetch effect
  useEffect(() => {
    const fetchInitial = async () => {
      if (!isIndexedDBInitialized) return;

      setIsLoading(true);

      // Priority: initialYear/initialMonth > value > current month
      // If value prop exists but no initialYear/initialMonth, don't reset to current month
      // (Navigation will happen when popup opens, not on mount)
      const hasValueProp = value && !initialYear && !initialMonth;
      
      if (!initialYear && !initialMonth && !hasValueProp) {
        // No initialYear/initialMonth and no value prop - use current month
        const currentBS = getCurrentBSDate();
        if (currentBS) {
          const approxBSYear = currentBS.bs_y;
          const approxBSMonth = currentBS.bs_m;

          let foundMonth: number | null = null;
          let foundData: CalendarData | null = null;

          try {
            const data = await fetchMonthData(approxBSYear, approxBSMonth);
            if (data && data.days) {
              const hasToday = data.days.some((d) => d.today);
              if (hasToday) {
                foundMonth = approxBSMonth;
                foundData = data;
                setCurrentBSYear(approxBSYear);
                setCurrentBSMonth(approxBSMonth);
              }
            }
          } catch (err) {
            console.error(err);
          }

          if (!foundMonth) {
            const searchMonths = [
              approxBSMonth,
              approxBSMonth - 1,
              approxBSMonth + 1,
            ];
            for (const m of searchMonths) {
              if (m < 1 || m > 12) continue;
              try {
                const data = await fetchMonthData(approxBSYear, m);
                if (data && data.days) {
                  const hasToday = data.days.some((d) => d.today);
                  if (hasToday) {
                    foundMonth = m;
                    foundData = data;
                    setCurrentBSYear(approxBSYear);
                    setCurrentBSMonth(m);
                    break;
                  }
                }
              } catch (err) {
                console.error(err);
              }
            }
          }

          if (!foundMonth) {
            foundMonth = approxBSMonth;
            setCurrentBSYear(approxBSYear);
            setCurrentBSMonth(approxBSMonth);
            foundData = await fetchMonthData(approxBSYear, approxBSMonth);
          }

          fetchMonthData(approxBSYear, Math.max(1, foundMonth - 1));
          fetchMonthData(approxBSYear, Math.min(12, foundMonth + 1));

          // If initial calendar mode is AD, set primary AD month
          if (initialCalendarMode === "ad" && foundData) {
            const currentData = foundData;
            if (
              currentData &&
              currentData.days &&
              currentData.days.length > 0
            ) {
              // Priority 1: Find today's date in the current BS month data
              const todayDay = currentData.days.find(
                (day) => day.today === true
              );
              if (todayDay && todayDay.ad) {
                setPrimaryADMonth(todayDay.ad.ad_m);
                setPrimaryADYear(todayDay.ad.ad_y);
              } else {
                // Priority 2: Use majority month as fallback
                const monthCounts: Record<string, number> = {};
                for (const day of currentData.days) {
                  if (day.ad && day.ad.date) {
                    const dateParts = day.ad.date.split("-");
                    if (dateParts.length === 3) {
                      const key = `${dateParts[0]}-${dateParts[1]}`;
                      monthCounts[key] = (monthCounts[key] || 0) + 1;
                    }
                  }
                }
                let maxCount = 0;
                let majorityMonth = "";
                for (const [key, count] of Object.entries(monthCounts)) {
                  if (count > maxCount) {
                    maxCount = count;
                    majorityMonth = key;
                  }
                }

                if (majorityMonth) {
                  const [y, m] = majorityMonth.split("-").map(Number);
                  setPrimaryADMonth(m);
                  setPrimaryADYear(y);
                }
              }
            }
          }

          setIsLoading(false);
          return;
        }
      }

      const data = await fetchMonthData(currentBSYear, currentBSMonth);
      if (!data) {
        await fetchYearData(currentBSYear);
      }

      const prevIndex = currentBSMonth - 1;
      const nextIndex = currentBSMonth + 1;
      if (prevIndex >= 1) fetchMonthData(currentBSYear, prevIndex);
      if (nextIndex <= 12) fetchMonthData(currentBSYear, nextIndex);

      // If initialYear/initialMonth are provided, set primary AD month/year for AD mode
      if (initialYear && initialMonth) {
        if (initialCalendarMode === "ad") {
          // Use the provided AD year/month directly
          setPrimaryADMonth(initialMonth);
          setPrimaryADYear(initialYear);
        } else {
          // For BS mode, find the majority AD month from the fetched BS month data
          if (data && data.days && data.days.length > 0) {
            const monthCounts: Record<string, number> = {};
            for (const day of data.days) {
              if (day.ad && day.ad.date) {
                const dateParts = day.ad.date.split("-");
                if (dateParts.length === 3) {
                  const key = `${dateParts[0]}-${dateParts[1]}`;
                  monthCounts[key] = (monthCounts[key] || 0) + 1;
                }
              }
            }
            let maxCount = 0;
            let majorityMonth = "";
            for (const [key, count] of Object.entries(monthCounts)) {
              if (count > maxCount) {
                maxCount = count;
                majorityMonth = key;
              }
            }

            if (majorityMonth) {
              const [y, m] = majorityMonth.split("-").map(Number);
              setPrimaryADMonth(m);
              setPrimaryADYear(y);
            }
          }
        }
      } else if (initialCalendarMode === "ad" && data) {
        // If no initialYear/month provided but AD mode, use majority month from data
        const currentData = data;
        if (currentData && currentData.days && currentData.days.length > 0) {
          // Priority 1: Find today's date in the current BS month data
          const todayDay = currentData.days.find((day) => day.today === true);
          if (todayDay && todayDay.ad) {
            setPrimaryADMonth(todayDay.ad.ad_m);
            setPrimaryADYear(todayDay.ad.ad_y);
          } else {
            // Priority 2: Use majority month as fallback
            const monthCounts: Record<string, number> = {};
            for (const day of currentData.days) {
              if (day.ad && day.ad.date) {
                const dateParts = day.ad.date.split("-");
                if (dateParts.length === 3) {
                  const key = `${dateParts[0]}-${dateParts[1]}`;
                  monthCounts[key] = (monthCounts[key] || 0) + 1;
                }
              }
            }
            let maxCount = 0;
            let majorityMonth = "";
            for (const [key, count] of Object.entries(monthCounts)) {
              if (count > maxCount) {
                maxCount = count;
                majorityMonth = key;
              }
            }

            if (majorityMonth) {
              const [y, m] = majorityMonth.split("-").map(Number);
              setPrimaryADMonth(m);
              setPrimaryADYear(y);
            }
          }
        }
      }

      setIsLoading(false);
    };
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIndexedDBInitialized]);

  // Helpers to get calendarData by current bs year/month
  const getCurrentKey = () => getKey(currentBSYear, currentBSMonth);
  const getDataForKey = (bsYear: number, bsMonth: number) =>
    calendarData[getKey(bsYear, bsMonth)];

  // Track previous value to only update when it actually changes
  // Initialize to undefined so first render processes the value
  const prevValueRef = useRef<string | undefined>(undefined);
  // Track if this is the initial mount (to prevent navigation on mount)
  const isInitialMountRef = useRef<boolean>(true);

  // Handle value prop: parse date and set selected date
  // IMPORTANT: value prop is ALWAYS in AD format, regardless of calendarMode
  useEffect(() => {
    if (!value) {
      prevValueRef.current = value;
      return;
    }

    // Wait for IndexedDB to initialize before navigating/fetching,
    // but we can still set the selected keys immediately
    if (!isIndexedDBInitialized) {
      // Set keys immediately even if DB not ready (for input display)
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const adYear = parseInt(dateParts[0], 10);
        const adMonth = parseInt(dateParts[1], 10);
        const adDay = parseInt(dateParts[2], 10);
        if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
          if (adYear >= 1921 && adYear <= 2032) {
            const adDateString = `${adYear}-${String(adMonth).padStart(
              2,
              "0"
            )}-${String(adDay).padStart(2, "0")}`;
            const bsDate = adToBS(adYear, adMonth, adDay);
            if (bsDate) {
              const bsDateString = `${bsDate.bs_y}-${String(
                bsDate.bs_m
              ).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
              setSelectedADKey(adDateString);
              setSelectedBSKey(bsDateString);
              setPrimaryADMonth(adMonth);
              setPrimaryADYear(adYear);
            }
          }
        }
      }
      prevValueRef.current = value;
      return;
    }

    // Only update if value actually changed
    if (prevValueRef.current === value) {
      return;
    }

    // Value is always in AD format - parse as AD date
    const dateParts = value.split("-");
    if (dateParts.length !== 3) {
      prevValueRef.current = value;
      return;
    }

    const adYear = parseInt(dateParts[0], 10);
    const adMonth = parseInt(dateParts[1], 10);
    const adDay = parseInt(dateParts[2], 10);

    if (isNaN(adYear) || isNaN(adMonth) || isNaN(adDay)) {
      prevValueRef.current = value;
      return;
    }

    // Validate AD date range
    if (adYear < 1921 || adYear > 2032) {
      console.warn(`AD value ${value} is out of supported range (1921-2032).`);
      prevValueRef.current = value;
      return;
    }

    // Value is in AD format - set selectedADKey directly
    const adDateString = `${adYear}-${String(adMonth).padStart(
      2,
      "0"
    )}-${String(adDay).padStart(2, "0")}`;

    // Convert AD to BS to get selectedBSKey
    const bsDate = adToBS(adYear, adMonth, adDay);
    if (!bsDate) {
      console.warn(`Failed to convert AD date ${value} to BS.`);
      prevValueRef.current = value;
      return;
    }

    const bsDateString = `${bsDate.bs_y}-${String(bsDate.bs_m).padStart(
      2,
      "0"
    )}-${String(bsDate.bs_d).padStart(2, "0")}`;

    // Always set the keys when value changes (they're derived from value)
    setSelectedADKey(adDateString);
    setSelectedBSKey(bsDateString);

    // Navigate to the correct BS month (API expects BS year/month)
    // BUT: Don't navigate on initial mount - navigation happens when popup opens
    // Only navigate if value actually changed (not initial mount)
    if (!isInitialMountRef.current) {
      const currentData = getDataForKey(currentBSYear, currentBSMonth);
      const needsNavigation =
        !currentData ||
        !currentData.days.find((d) => d.ad?.date === adDateString);

      if (needsNavigation) {
        // Navigate to the BS month that contains this AD date
        setCurrentBSMonth(bsDate.bs_m);
        setCurrentBSYear(bsDate.bs_y);
        // Fetch data for the month
        fetchMonthData(bsDate.bs_y, bsDate.bs_m);
        onMonthChange?.(bsDate.bs_y, bsDate.bs_m);
      }
    } else {
      // Mark that initial mount is complete
      isInitialMountRef.current = false;
    }

    // Set primary AD month (important for AD mode display)
    setPrimaryADMonth(adMonth);
    setPrimaryADYear(adYear);

    // Update ref after processing
    prevValueRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    isIndexedDBInitialized,
    // Note: We intentionally don't include currentBSYear/currentBSMonth in deps
    // because we only want to run this effect when value changes, not when user navigates
    // getDataForKey is stable and uses currentBSYear/currentBSMonth internally
  ]);

  const prevYearRef = useRef<number | undefined>(undefined);
  const prevMonthRef = useRef<number | undefined>(undefined);

  // Handle year/month props: convert AD year/month to BS and navigate
  // IMPORTANT: year/month props are ALWAYS in AD format, regardless of calendarMode
  useEffect(() => {
    // Skip if IndexedDB not initialized or props not provided
    if (!isIndexedDBInitialized || !initialYear || !initialMonth) {
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Only update if props actually changed
    if (
      prevYearRef.current === initialYear &&
      prevMonthRef.current === initialMonth
    ) {
      return;
    }

    // Validate AD month range
    if (initialMonth < 1 || initialMonth > 12) {
      console.warn(`AD month ${initialMonth} is out of range (1-12).`);
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Validate AD year range
    if (initialYear < 1921 || initialYear > 2032) {
      console.warn(
        `AD year ${initialYear} is out of supported range (1921-2032).`
      );
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Convert AD year/month to BS
    const bsDate = adToBS(initialYear, initialMonth, 15); // Use day 15 as anchor
    if (!bsDate) {
      console.warn(
        `Failed to convert AD date ${initialYear}-${initialMonth}-15 to BS.`
      );
      prevYearRef.current = initialYear;
      prevMonthRef.current = initialMonth;
      return;
    }

    // Set primary AD month/year for AD mode
    setPrimaryADMonth(initialMonth);
    setPrimaryADYear(initialYear);

    // Navigate based on calendarMode
    if (calendarMode === "ad") {
      // AD mode: navigate to AD month (fetch BS months that contain this AD month)
      setCurrentBSYear(bsDate.bs_y);
      setCurrentBSMonth(bsDate.bs_m);
      fetchMonthData(bsDate.bs_y, bsDate.bs_m);
      onMonthChange?.(initialYear, initialMonth);
    } else {
      // BS mode: navigate to BS month
      setCurrentBSYear(bsDate.bs_y);
      setCurrentBSMonth(bsDate.bs_m);
      fetchMonthData(bsDate.bs_y, bsDate.bs_m);
      onMonthChange?.(bsDate.bs_y, bsDate.bs_m);
    }

    prevYearRef.current = initialYear;
    prevMonthRef.current = initialMonth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialYear,
    initialMonth,
    calendarMode,
    isIndexedDBInitialized,
    // Note: We intentionally don't include currentBSYear/currentBSMonth in deps
    // because we only want to run this effect when props change, not when user navigates
  ]);

  // Helper to get the correct month/year for dropdown highlighting
  const getDropdownDisplayValues = () => {
    if (calendarMode === "bs") {
      return {
        selectedMonth: currentBSMonth,
        selectedYear: currentBSYear,
      };
    } else {
      const currentData = getDataForKey(currentBSYear, currentBSMonth);
      if (currentData && currentData.days && currentData.days.length > 0) {
        const dayWithAd = currentData.days.find((d) => d.ad && d.ad.date);
        if (dayWithAd) {
          return {
            selectedMonth: dayWithAd.ad.ad_m,
            selectedYear: dayWithAd.ad.ad_y,
          };
        }
      }
      return {
        selectedMonth: currentBSMonth,
        selectedYear: currentBSYear,
      };
    }
  };

  // Helper to get AD month range for the current BS month
  const getADMonthRange = (): string | null => {
    if (calendarMode === "ad") return null;

    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData || !currentData.days || currentData.days.length === 0) {
      return null;
    }

    let firstAdMonth = -1;
    let firstAdYear = -1;
    let lastAdMonth = -1;
    let lastAdYear = -1;

    for (const day of currentData.days) {
      if (day.ad && day.ad.date) {
        const dateParts = day.ad.date.split("-");
        if (dateParts.length === 3) {
          const month = parseInt(dateParts[1], 10);
          const year = parseInt(dateParts[0], 10);

          if (firstAdMonth === -1) {
            firstAdMonth = month;
            firstAdYear = year;
          }
          lastAdMonth = month;
          lastAdYear = year;
        }
      }
    }

    if (firstAdMonth === -1) return null;

    const monthsEn = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    if (firstAdMonth === lastAdMonth && firstAdYear === lastAdYear) {
      return `(${monthsEn[firstAdMonth - 1]}, ${firstAdYear})`;
    } else {
      // Show both years if they differ, otherwise show single year
      const yearDisplay =
        firstAdYear === lastAdYear
          ? `${firstAdYear}`
          : `${firstAdYear}-${lastAdYear}`;
      return `(${monthsEn[firstAdMonth - 1]}/${
        monthsEn[lastAdMonth - 1]
      }, ${yearDisplay})`;
    }
  };

  // Get displayed month/year depending on mode
  const getDisplayMonthYear = () => {
    const currentData = getDataForKey(currentBSYear, currentBSMonth);
    if (!currentData || !currentData.days || currentData.days.length === 0) {
      const months = [
        "बैशाख",
        "जेठ",
        "असार",
        "श्रावण",
        "भदौ",
        "आश्विन",
        "कार्तिक",
        "मंसिर",
        "पुष",
        "माघ",
        "फागुन",
        "चैत्र",
      ];
      const monthsEn = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      const toNepaliNumber = (num: number): string => {
        const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
        return num
          .toString()
          .replace(/[0-9]/g, (match) => nepaliDigits[parseInt(match)]);
      };

      if (calendarMode === "bs") {
        return {
          month: months[currentBSMonth - 1],
          year: toNepaliNumber(currentBSYear),
          monthNum: currentBSMonth,
          yearNum: currentBSYear,
        };
      } else {
        // In AD mode, if no data and primary AD month not set, convert BS to AD or use current date
        if (primaryADMonth !== null && primaryADYear !== null) {
          return {
            month: monthsEn[primaryADMonth - 1],
            year: primaryADYear.toString(),
            monthNum: primaryADMonth,
            yearNum: primaryADYear,
          };
        }

        // Fallback: convert current BS date to AD, or use today's date
        try {
          const bsDate = {
            bs_y: currentBSYear,
            bs_m: currentBSMonth,
            bs_d: 15,
          };
          const adDate = bsToAD(bsDate.bs_y, bsDate.bs_m, bsDate.bs_d);
          if (adDate) {
            return {
              month: monthsEn[adDate.ad_m - 1],
              year: adDate.ad_y.toString(),
              monthNum: adDate.ad_m,
              yearNum: adDate.ad_y,
            };
          }
        } catch (error) {
          // If conversion fails, use current AD date
          const today = new Date();
          return {
            month: monthsEn[today.getMonth()],
            year: today.getFullYear().toString(),
            monthNum: today.getMonth() + 1,
            yearNum: today.getFullYear(),
          };
        }

        // Final fallback: use current AD date
        const today = new Date();
        return {
          month: monthsEn[today.getMonth()],
          year: today.getFullYear().toString(),
          monthNum: today.getMonth() + 1,
          yearNum: today.getFullYear(),
        };
      }
    }

    const months = [
      "बैशाख",
      "जेठ",
      "असार",
      "श्रावण",
      "भदौ",
      "आश्विन",
      "कार्तिक",
      "मंसिर",
      "पुष",
      "माघ",
      "फागुन",
      "चैत्र",
    ];
    const monthsEn = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const toNepaliNumber = (num: number): string => {
      const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
      return num
        .toString()
        .replace(/[0-9]/g, (match) => nepaliDigits[parseInt(match)]);
    };

    if (calendarMode === "bs") {
      return {
        month: months[currentBSMonth - 1],
        year: toNepaliNumber(currentBSYear),
        monthNum: currentBSMonth,
        yearNum: currentBSYear,
      };
    } else {
      // In AD mode, prioritize primary AD month, then selected date's AD month, then majority month
      let adYear = currentBSYear;
      let adMonth = currentBSMonth;

      // First, use primary AD month if set (this is the month we're navigating/viewing)
      // Trust the primary AD month even if data isn't loaded yet - it's set when navigating/inputting
      if (primaryADMonth !== null && primaryADYear !== null) {
        // Check if this AD month exists in current BS month (if data is loaded)
        // But still use it even if not found - it might be loading
        const dayInMonth = currentData.days?.find(
          (d) =>
            d.ad && d.ad.ad_y === primaryADYear && d.ad.ad_m === primaryADMonth
        );
        // Use primary AD month regardless - it's the source of truth for what we're viewing
        return {
          month: monthsEn[primaryADMonth - 1],
          year: primaryADYear.toString(),
          monthNum: primaryADMonth,
          yearNum: primaryADYear,
        };
      }

      // Second, try to use the selected date's AD month
      if (selectedADKey) {
        const dateParts = selectedADKey.split("-");
        if (dateParts.length === 3) {
          const selYear = parseInt(dateParts[0], 10);
          const selMonth = parseInt(dateParts[1], 10);
          // Check if selected date is in current BS month
          const dayInCurrentMonth = currentData.days.find(
            (d) => d.ad?.date === selectedADKey
          );
          if (dayInCurrentMonth) {
            return {
              month: monthsEn[selMonth - 1],
              year: selYear.toString(),
              monthNum: selMonth,
              yearNum: selYear,
            };
          }
        }
      }

      // Third, find the AD month with the most days in this BS month
      const monthCounts: Record<string, number> = {};
      for (const day of currentData.days) {
        if (day.ad && day.ad.date) {
          const dateParts = day.ad.date.split("-");
          if (dateParts.length === 3) {
            const key = `${dateParts[0]}-${dateParts[1]}`;
            monthCounts[key] = (monthCounts[key] || 0) + 1;
          }
        }
      }

      // Find the month with the most days
      let maxCount = 0;
      let majorityMonth = "";
      for (const [key, count] of Object.entries(monthCounts)) {
        if (count > maxCount) {
          maxCount = count;
          majorityMonth = key;
        }
      }

      if (majorityMonth) {
        const [y, m] = majorityMonth.split("-").map(Number);
        adYear = y;
        adMonth = m;
      } else {
        // Fallback: use first day's AD month
        for (const day of currentData.days) {
          if (day.ad && day.ad.date) {
            const dateParts = day.ad.date.split("-");
            if (dateParts.length === 3) {
              adYear = parseInt(dateParts[0], 10);
              adMonth = parseInt(dateParts[1], 10);
              break;
            }
          }
        }
      }

      return {
        month: monthsEn[adMonth - 1],
        year: adYear.toString(),
        monthNum: adMonth,
        yearNum: adYear,
      };
    }
  };

  // Handle clicking a day
  const handleDayClick = async (day: CalendarDay) => {
    setSelectedBSKey(day.bs.date);
    setSelectedADKey(day.ad.date);
    const dateStr = calendarMode === "bs" ? day.bs.date : day.ad.date;
    setSelectedDate(dateStr);
    
    // Extract BS month/year from the selected day
    const selectedBSYear = day.bs.bs_y;
    const selectedBSMonth = day.bs.bs_m;
    
    // Navigate to the BS month containing the selected date if it's different from current month
    // This ensures the calendar shows the correct month after selection
    if (
      selectedBSYear !== currentBSYear ||
      selectedBSMonth !== currentBSMonth
    ) {
      setCurrentBSYear(selectedBSYear);
      setCurrentBSMonth(selectedBSMonth);
      // Fetch data if missing
      if (!getDataForKey(selectedBSYear, selectedBSMonth)) {
        await fetchMonthData(selectedBSYear, selectedBSMonth);
      }
      // Notify parent component of month change
      if (calendarMode === "bs") {
        onMonthChange?.(selectedBSYear, selectedBSMonth);
      } else if (calendarMode === "ad" && day.ad) {
        // In AD mode, notify with AD month/year
        onMonthChange?.(day.ad.ad_y, day.ad.ad_m);
      }
    }
    
    // Set primary AD month when selecting a date in AD mode
    if (calendarMode === "ad" && day.ad) {
      setPrimaryADMonth(day.ad.ad_m);
      setPrimaryADYear(day.ad.ad_y);
    }
    onDateSelect?.(day);
  };

  // Handle prev/next depending on active mode
  const handlePrevMonth = async () => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        // Clear originalBSMonth when user explicitly navigates - this ensures toggle back to BS uses current month
        setOriginalBSMonth(null);
        setOriginalBSYear(null);
        
        if (currentBSMonth === 1) {
          const newYear = currentBSYear - 1;
          setCurrentBSYear(newYear);
          if (!getDataForKey(newYear, 12)) {
            await fetchMonthData(newYear, 12);
          }
          setCurrentBSMonth(12);
          onMonthChange?.(newYear, 12);
        } else {
          const newMonth = currentBSMonth - 1;
          if (!getDataForKey(currentBSYear, newMonth)) {
            await fetchMonthData(currentBSYear, newMonth);
          }
          setCurrentBSMonth(newMonth);
          onMonthChange?.(currentBSYear, newMonth);
        }
      } else {
        // AD mode: navigate by AD months
        // Clear originalBSMonth when navigating in AD mode - this ensures toggle back to BS uses current BS month
        setOriginalBSMonth(null);
        setOriginalBSYear(null);
        
        const display = getDisplayMonthYear();
        let targetAdYear = display.yearNum as number;
        let targetAdMonth = (display.monthNum as number) - 1;

        if (targetAdMonth === 0) {
          targetAdMonth = 12;
          targetAdYear = targetAdYear - 1;
        }

        // Set primary AD month for navigation
        setPrimaryADMonth(targetAdMonth);
        setPrimaryADYear(targetAdYear);

        // Find BS month that contains this AD month (use day 1 of the AD month)
        const bsDate = adToBS(targetAdYear, targetAdMonth, 1);
        if (bsDate) {
          // Fetch the BS month data
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }

          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);
          onMonthChange?.(targetAdYear, targetAdMonth);
        } else {
          // Fallback: try to find using search
          const found = await findBSMonthForAD(targetAdYear, targetAdMonth);
          if (found) {
            setCurrentBSYear(found.bsYear);
            setCurrentBSMonth(found.bsMonth);
            setPrimaryADMonth(targetAdMonth);
            setPrimaryADYear(targetAdYear);
            onMonthChange?.(targetAdYear, targetAdMonth);
          }
        }
      }

      // Prefetch adjacent months
      if (calendarMode === "bs") {
        const prevM =
          currentBSMonth === 1
            ? { y: currentBSYear - 1, m: 12 }
            : { y: currentBSYear, m: currentBSMonth - 1 };
        const nextM =
          currentBSMonth === 12
            ? { y: currentBSYear + 1, m: 1 }
            : { y: currentBSYear, m: currentBSMonth + 1 };
        fetchMonthData(prevM.y, prevM.m);
        fetchMonthData(nextM.y, nextM.m);
      } else {
        // Prefetch next/prev AD months
        const displayAfter = getDisplayMonthYear();
        if (displayAfter && displayAfter.monthNum && displayAfter.yearNum) {
          // Prefetch next AD month
          let nextAdMonth = displayAfter.monthNum + 1;
          let nextAdYear = displayAfter.yearNum;
          if (nextAdMonth === 13) {
            nextAdMonth = 1;
            nextAdYear = nextAdYear + 1;
          }
          const nextBS = adToBS(nextAdYear, nextAdMonth, 1);
          if (nextBS) {
            fetchMonthData(nextBS.bs_y, nextBS.bs_m);
          }

          // Prefetch prev AD month
          let prevAdMonth = displayAfter.monthNum - 1;
          let prevAdYear = displayAfter.yearNum;
          if (prevAdMonth === 0) {
            prevAdMonth = 12;
            prevAdYear = prevAdYear - 1;
          }
          const prevBS = adToBS(prevAdYear, prevAdMonth, 1);
          if (prevBS) {
            fetchMonthData(prevBS.bs_y, prevBS.bs_m);
          }
        }
      }
    } finally {
      setIsMonthLoading(false);
    }
  };

  const handleNextMonth = async () => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        // Clear originalBSMonth when user explicitly navigates - this ensures toggle back to BS uses current month
        setOriginalBSMonth(null);
        setOriginalBSYear(null);
        
        if (currentBSMonth === 12) {
          const newYear = currentBSYear + 1;
          setCurrentBSYear(newYear);
          if (!getDataForKey(newYear, 1)) {
            await fetchMonthData(newYear, 1);
          }
          setCurrentBSMonth(1);
          onMonthChange?.(newYear, 1);
        } else {
          const newMonth = currentBSMonth + 1;
          if (!getDataForKey(currentBSYear, newMonth)) {
            await fetchMonthData(currentBSYear, newMonth);
          }
          setCurrentBSMonth(newMonth);
          onMonthChange?.(currentBSYear, newMonth);
        }
      } else {
        // AD mode: navigate by AD months
        // Clear originalBSMonth when navigating in AD mode - this ensures toggle back to BS uses current BS month
        setOriginalBSMonth(null);
        setOriginalBSYear(null);
        
        const display = getDisplayMonthYear();

        if (
          !display ||
          display.monthNum === undefined ||
          display.yearNum === undefined
        ) {
          console.error("AD Mode - Invalid display data");
          setIsMonthLoading(false);
          return;
        }

        let targetAdYear = display.yearNum;
        let targetAdMonth = display.monthNum + 1;

        if (targetAdMonth === 13) {
          targetAdMonth = 1;
          targetAdYear = targetAdYear + 1;
        }

        // Set primary AD month for navigation
        setPrimaryADMonth(targetAdMonth);
        setPrimaryADYear(targetAdYear);

        // Find BS month that contains this AD month (use day 1 of the AD month)
        const bsDate = adToBS(targetAdYear, targetAdMonth, 1);
        if (bsDate) {
          // Fetch the BS month data
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }

          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);
          onMonthChange?.(targetAdYear, targetAdMonth);
        } else {
          // Fallback: try to find using search
          const found = await findBSMonthForAD(targetAdYear, targetAdMonth);
          if (found) {
            setCurrentBSYear(found.bsYear);
            setCurrentBSMonth(found.bsMonth);
            setPrimaryADMonth(targetAdMonth);
            setPrimaryADYear(targetAdYear);
            onMonthChange?.(targetAdYear, targetAdMonth);
          }
        }
      }

      // Prefetch adjacent months
      if (calendarMode === "bs") {
        const prevM =
          currentBSMonth === 1
            ? { y: currentBSYear - 1, m: 12 }
            : { y: currentBSYear, m: currentBSMonth - 1 };
        const nextM =
          currentBSMonth === 12
            ? { y: currentBSYear + 1, m: 1 }
            : { y: currentBSYear, m: currentBSMonth + 1 };
        fetchMonthData(prevM.y, prevM.m);
        fetchMonthData(nextM.y, nextM.m);
      } else {
        // Prefetch next/prev AD months
        const displayAfter = getDisplayMonthYear();
        if (displayAfter && displayAfter.monthNum && displayAfter.yearNum) {
          // Prefetch next AD month
          let nextAdMonth = displayAfter.monthNum + 1;
          let nextAdYear = displayAfter.yearNum;
          if (nextAdMonth === 13) {
            nextAdMonth = 1;
            nextAdYear = nextAdYear + 1;
          }
          const nextBS = adToBS(nextAdYear, nextAdMonth, 1);
          if (nextBS) {
            fetchMonthData(nextBS.bs_y, nextBS.bs_m);
          }

          // Prefetch prev AD month
          let prevAdMonth = displayAfter.monthNum - 1;
          let prevAdYear = displayAfter.yearNum;
          if (prevAdMonth === 0) {
            prevAdMonth = 12;
            prevAdYear = prevAdYear - 1;
          }
          const prevBS = adToBS(prevAdYear, prevAdMonth, 1);
          if (prevBS) {
            fetchMonthData(prevBS.bs_y, prevBS.bs_m);
          }
        }
      }
    } finally {
      setIsMonthLoading(false);
    }
  };

  const handleCalendarModeChange = async (mode: "bs" | "ad") => {
    if (mode === calendarMode) return;
    
    // Prevent rapid toggles
    if (isTogglingRef.current) return;
    isTogglingRef.current = true;

    // Mark that user has manually toggled (so props won't override)
    hasUserToggledMode.current = true;

    // If switching to AD mode
    if (mode === "ad") {
      let targetADYear: number | null = null;
      let targetADMonth: number | null = null;
      let needsNavigation = false;
      
      // Ensure current BS month data is loaded
      if (!getDataForKey(currentBSYear, currentBSMonth)) {
        await fetchMonthData(currentBSYear, currentBSMonth);
      }
      
      const currentData = getDataForKey(currentBSYear, currentBSMonth);
      const todayDates = getTodayDates();
      
      // Priority 1: Selected date exists → ALWAYS navigate to selected date's month first, then use its AD month
      if (selectedBSKey) {
        const dateParts = selectedBSKey.split("-");
        if (dateParts.length === 3) {
          const selectedBSYear = parseInt(dateParts[0], 10);
          const selectedBSMonth = parseInt(dateParts[1], 10);
          
          // Check if selected date is in current BS month
          const isSelectedInCurrentMonth = selectedBSYear === currentBSYear && selectedBSMonth === currentBSMonth;
          
          if (!isSelectedInCurrentMonth) {
            // Selected date is NOT in current month → navigate to selected date's month first
            needsNavigation = true;
            setCurrentBSYear(selectedBSYear);
            setCurrentBSMonth(selectedBSMonth);
            // Fetch data for selected date's month
            if (!getDataForKey(selectedBSYear, selectedBSMonth)) {
              await fetchMonthData(selectedBSYear, selectedBSMonth);
            }
            // Update currentData to selected date's month
            const selectedMonthData = getDataForKey(selectedBSYear, selectedBSMonth);
            if (selectedMonthData && selectedADKey) {
              // Use selected date's AD month
              const adParts = selectedADKey.split("-");
              if (adParts.length === 3) {
                targetADYear = parseInt(adParts[0], 10);
                targetADMonth = parseInt(adParts[1], 10);
                setPrimaryADMonth(targetADMonth);
                setPrimaryADYear(targetADYear);
              }
            }
          } else if (selectedADKey && currentData) {
            // Selected date IS in current month → use selected date's AD month
            const adParts = selectedADKey.split("-");
            if (adParts.length === 3) {
              targetADYear = parseInt(adParts[0], 10);
              targetADMonth = parseInt(adParts[1], 10);
              setPrimaryADMonth(targetADMonth);
              setPrimaryADYear(targetADYear);
            }
          }
        }
      }
      
      // Priority 2: Value prop exists AND is in current BS month → use value prop's AD month
      if (!targetADYear && value) {
        const valueBSDate = adToBS(value); // value is in AD format
        if (valueBSDate) {
          const isValueInCurrentMonth = valueBSDate.bs_y === currentBSYear && valueBSDate.bs_m === currentBSMonth;
          if (isValueInCurrentMonth) {
            // Value is in current BS month → use value prop's AD month
            const dateParts = value.split("-");
            if (dateParts.length === 3) {
              targetADYear = parseInt(dateParts[0], 10);
              targetADMonth = parseInt(dateParts[1], 10);
              setPrimaryADMonth(targetADMonth);
              setPrimaryADYear(targetADYear);
            }
          }
          // If value is NOT in current month, ignore it (user has navigated)
        }
      }
      
      // Priority 3: Today's date exists AND is in current BS month → use today's AD month
      if (!targetADYear && todayDates.bsDate && currentData) {
        const isTodayInMonth = isDateInBSMonth(todayDates.bsDate, currentBSYear, currentBSMonth, calendarData);
        if (isTodayInMonth && todayDates.adDate) {
          // Today is in current BS month → use today's AD month
          const dateParts = todayDates.adDate.split("-");
          if (dateParts.length === 3) {
            targetADYear = parseInt(dateParts[0], 10);
            targetADMonth = parseInt(dateParts[1], 10);
            setPrimaryADMonth(targetADMonth);
            setPrimaryADYear(targetADYear);
          }
        }
      }
      
      // Priority 4: No selected date, no value in current month, no today → use BS month's starting AD month
      if (!targetADYear) {
        const startAD = getBSMonthStartADMonth(currentBSYear, currentBSMonth);
        if (startAD) {
          targetADYear = startAD.adYear;
          targetADMonth = startAD.adMonth;
          setPrimaryADMonth(targetADMonth);
          setPrimaryADYear(targetADYear);
        }
      }
      
      // Fetch data for the determined AD month
      if (targetADYear && targetADMonth) {
        const bsDate = adToBS(targetADYear, targetADMonth, 15);
        if (bsDate) {
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }
          // Fetch all BS months for this AD month if function exists
          if (fetchAllBSMonthsForADMonth) {
            if (
              targetADYear >= 1921 &&
              targetADYear <= 2032 &&
              targetADMonth >= 1 &&
              targetADMonth <= 12
            ) {
              await fetchAllBSMonthsForADMonth(targetADYear, targetADMonth);
            }
          }
        }
      }
    }

    // If switching back to BS mode
    if (mode === "bs") {
      let targetBSYear: number | null = null;
      let targetBSMonth: number | null = null;
      let needsNavigation = false;
      
      // Get current AD month (from primaryADMonth or getDisplayMonthYear)
      let currentAdYear = primaryADYear;
      let currentAdMonth = primaryADMonth;
      if (currentAdYear === null || currentAdMonth === null) {
        const displayInfo = getDisplayMonthYear();
        if (displayInfo && displayInfo.yearNum !== undefined && displayInfo.monthNum !== undefined) {
          currentAdYear = displayInfo.yearNum;
          currentAdMonth = displayInfo.monthNum;
        }
      }
      
      if (currentAdYear === null || currentAdMonth === null) {
        // Fallback: use current BS month
        targetBSYear = currentBSYear;
        targetBSMonth = currentBSMonth;
      } else {
        // Ensure data is loaded for current AD month (need to find which BS month contains it)
        const bsDateForAD = adToBS(currentAdYear, currentAdMonth, 15);
        if (bsDateForAD && !getDataForKey(bsDateForAD.bs_y, bsDateForAD.bs_m)) {
          await fetchMonthData(bsDateForAD.bs_y, bsDateForAD.bs_m);
        }
        
        const todayDates = getTodayDates();
        
        // Priority 1: Selected date exists → ALWAYS navigate to selected date's month first, then use its BS month
        if (selectedADKey) {
          const dateParts = selectedADKey.split("-");
          if (dateParts.length === 3) {
            const selectedADYear = parseInt(dateParts[0], 10);
            const selectedADMonth = parseInt(dateParts[1], 10);
            
            // Check if selected date is in current AD month
            const isSelectedInCurrentMonth = selectedADYear === currentAdYear && selectedADMonth === currentAdMonth;
            
            if (!isSelectedInCurrentMonth) {
              // Selected date is NOT in current month → navigate to selected date's month first
              needsNavigation = true;
              if (selectedBSKey) {
                const bsParts = selectedBSKey.split("-");
                if (bsParts.length === 3) {
                  targetBSYear = parseInt(bsParts[0], 10);
                  targetBSMonth = parseInt(bsParts[1], 10);
                  setCurrentBSYear(targetBSYear);
                  setCurrentBSMonth(targetBSMonth);
                  // Fetch data for selected date's month
                  if (!getDataForKey(targetBSYear, targetBSMonth)) {
                    await fetchMonthData(targetBSYear, targetBSMonth);
                  }
                }
              }
            } else if (selectedBSKey) {
              // Selected date IS in current month → use selected date's BS month
              const bsParts = selectedBSKey.split("-");
              if (bsParts.length === 3) {
                targetBSYear = parseInt(bsParts[0], 10);
                targetBSMonth = parseInt(bsParts[1], 10);
              }
            }
          }
        }
        
        // Priority 2: Value prop exists AND is in current AD month → use value prop's BS month
        if (!targetBSYear && value) {
          const dateParts = value.split("-");
          if (dateParts.length === 3) {
            const valueADYear = parseInt(dateParts[0], 10);
            const valueADMonth = parseInt(dateParts[1], 10);
            const isValueInCurrentMonth = valueADYear === currentAdYear && valueADMonth === currentAdMonth;
            if (isValueInCurrentMonth) {
              // Value is in current AD month → use value prop's BS month
              const valueBSDate = adToBS(value);
              if (valueBSDate) {
                targetBSYear = valueBSDate.bs_y;
                targetBSMonth = valueBSDate.bs_m;
              }
            }
            // If value is NOT in current month, ignore it (user has navigated)
          }
        }
        
        // Priority 3: Today's date exists AND is in current AD month → use today's BS month
        if (!targetBSYear && todayDates.adDate) {
          const dateParts = todayDates.adDate.split("-");
          if (dateParts.length === 3) {
            const todayADYear = parseInt(dateParts[0], 10);
            const todayADMonth = parseInt(dateParts[1], 10);
            const isTodayInMonth = todayADYear === currentAdYear && todayADMonth === currentAdMonth;
            if (isTodayInMonth && todayDates.bsDate) {
              // Today is in current AD month → use today's BS month
              const bsParts = todayDates.bsDate.split("-");
              if (bsParts.length === 3) {
                targetBSYear = parseInt(bsParts[0], 10);
                targetBSMonth = parseInt(bsParts[1], 10);
              }
            }
          }
        }
        
        // Priority 4: Check if current AD month is contained in current BS month data
        // This ensures we restore the BS month we were viewing before toggling to AD
        if (!targetBSYear) {
          const currentBSData = getDataForKey(currentBSYear, currentBSMonth);
          if (currentBSData && currentBSData.days) {
            // Check if the AD month is contained in the current BS month
            const hasADMonthInCurrentBS = currentBSData.days.some(day => 
              day.ad && day.ad.ad_y === currentAdYear && day.ad.ad_m === currentAdMonth
            );
            if (hasADMonthInCurrentBS) {
              // AD month is in current BS month → restore current BS month
              targetBSYear = currentBSYear;
              targetBSMonth = currentBSMonth;
            }
          }
        }
        
        // Priority 5: No selected date, no value in current month, no today, AD not in current BS → find BS month where AD month starts
        if (!targetBSYear) {
          const startBS = getADMonthStartBSMonth(currentAdYear, currentAdMonth);
          if (startBS) {
            targetBSYear = startBS.bsYear;
            targetBSMonth = startBS.bsMonth;
          }
        }
      }
      
      // Set the target BS month
      if (targetBSYear && targetBSMonth) {
        setCurrentBSYear(targetBSYear);
        setCurrentBSMonth(targetBSMonth);
        if (!getDataForKey(targetBSYear, targetBSMonth)) {
          await fetchMonthData(targetBSYear, targetBSMonth);
        }
      }
      
      // Update selected date keys: if selectedADKey exists, ensure selectedBSKey is set
      if (selectedADKey && !selectedBSKey) {
        const dateParts = selectedADKey.split("-");
        if (dateParts.length === 3) {
          const adYear = parseInt(dateParts[0], 10);
          const adMonth = parseInt(dateParts[1], 10);
          const adDay = parseInt(dateParts[2], 10);
          if (!isNaN(adYear) && !isNaN(adMonth) && !isNaN(adDay)) {
            const bsDate = adToBS(adYear, adMonth, adDay);
            if (bsDate) {
              const bsDateString = `${bsDate.bs_y}-${String(bsDate.bs_m).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
              setSelectedBSKey(bsDateString);
            }
          }
        }
      }
    }

    setCalendarMode(mode);
    onCalendarModeChange?.(mode);
    
    // Update selected date keys if needed
    if (mode === "ad" && selectedBSKey && !selectedADKey) {
      const dateParts = selectedBSKey.split("-");
      if (dateParts.length === 3) {
        const bsYear = parseInt(dateParts[0], 10);
        const bsMonth = parseInt(dateParts[1], 10);
        const bsDay = parseInt(dateParts[2], 10);
        if (!isNaN(bsYear) && !isNaN(bsMonth) && !isNaN(bsDay)) {
          const adDate = bsToAD(bsYear, bsMonth, bsDay);
          if (adDate) {
            const adDateString = `${adDate.ad_y}-${String(adDate.ad_m).padStart(2, "0")}-${String(adDate.ad_d).padStart(2, "0")}`;
            setSelectedADKey(adDateString);
          }
        }
      }
    }
    
    // Reset toggle flag after all operations complete
    isTogglingRef.current = false;
  };

  // Sync with prop only on mount or if prop changes and user hasn't manually toggled
  useEffect(() => {
    // Only sync if user hasn't manually toggled the mode
    if (!hasUserToggledMode.current) {
      // Only update if different to avoid unnecessary re-renders
      if (initialCalendarMode !== calendarMode) {
        setCalendarMode(initialCalendarMode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCalendarMode]); // Only sync when prop changes, not when internal state changes

  const handleMonthSelect = async (month: number) => {
    setIsMonthLoading(true);
    try {
      if (calendarMode === "bs") {
        // BS mode: treat selection as BS month
        setCurrentBSMonth(month);
        setShowMonthDropdown(false);
        // Fetch data if missing
        if (!getDataForKey(currentBSYear, month)) {
          await fetchMonthData(currentBSYear, month);
        }
        onMonthChange?.(currentBSYear, month);
      } else {
        // AD mode: treat selection as AD month
        // Use current AD year (from primaryADYear or current display)
        const displayInfo = getDisplayMonthYear();
        const adYear = primaryADYear ?? displayInfo.yearNum;
        const adMonth = month; // This is the AD month selected

        // Convert AD month to BS using day 15 as anchor
        const bsDate = adToBS(adYear, adMonth, 15);
        if (bsDate) {
          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);
          setShowMonthDropdown(false);

          // Set primary AD month/year for AD mode display
          setPrimaryADMonth(adMonth);
          setPrimaryADYear(adYear);

          // Fetch BS month data if missing
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }

          // Fetch all BS months for this AD month (for adModeDays)
          if (fetchAllBSMonthsForADMonth) {
            fetchAllBSMonthsForADMonth(adYear, adMonth).catch((err) => {
              console.error("Error fetching AD month days:", err);
            });
          }

          onMonthChange?.(adYear, adMonth);
        } else {
          setShowMonthDropdown(false);
          console.error(
            `Failed to convert AD date ${adYear}-${adMonth}-15 to BS`
          );
        }
      }
    } finally {
      setIsMonthLoading(false);
    }
  };

  const handleYearSelect = async (year: number) => {
    setIsYearLoading(true);
    try {
      if (calendarMode === "bs") {
        // BS mode: treat selection as BS year
        setCurrentBSYear(year);
        setShowYearDropdown(false);
        // Fetch data if missing
        if (!getDataForKey(year, currentBSMonth)) {
          await fetchMonthData(year, currentBSMonth);
        }
        onMonthChange?.(year, currentBSMonth);
      } else {
        // AD mode: treat selection as AD year
        // Use current AD month (from primaryADMonth or current display)
        const displayInfo = getDisplayMonthYear();
        const adYear = year; // This is the AD year selected
        const adMonth = primaryADMonth ?? displayInfo.monthNum;

        // Convert AD month/year to BS using day 15 as anchor
        const bsDate = adToBS(adYear, adMonth, 15);
        if (bsDate) {
          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);
          setShowYearDropdown(false);

          // Set primary AD month/year for AD mode display
          setPrimaryADMonth(adMonth);
          setPrimaryADYear(adYear);

          // Fetch BS month data if missing
          if (!getDataForKey(bsDate.bs_y, bsDate.bs_m)) {
            await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
          }

          // Fetch all BS months for this AD month (for adModeDays)
          if (fetchAllBSMonthsForADMonth) {
            fetchAllBSMonthsForADMonth(adYear, adMonth).catch((err) => {
              console.error("Error fetching AD month days:", err);
            });
          }

          onMonthChange?.(adYear, adMonth);
        } else {
          setShowYearDropdown(false);
          console.error(
            `Failed to convert AD date ${adYear}-${adMonth}-15 to BS`
          );
        }
      }
    } finally {
      setIsYearLoading(false);
    }
  };

  // Handle input date parsing and fetching
  const handleInputDate = useCallback(
    async (dateString: string, mode: "bs" | "ad") => {
      const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
      const match = dateString.match(datePattern);

      if (!match) return null;

      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const day = parseInt(match[3], 10);

      if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

      setIsInputLoading(true);

      try {
        if (mode === "bs") {
          // Validate BS date
          if (year < 1978 || year > 2099) {
            console.error("BS year out of range");
            return null;
          }
          if (month < 1 || month > 12) {
            console.error("Invalid BS month");
            return null;
          }

          const bsDateString = `${year}-${String(month).padStart(
            2,
            "0"
          )}-${String(day).padStart(2, "0")}`;

          // IMPORTANT: Set current BS year/month FIRST so calendar navigates immediately
          setCurrentBSYear(year);
          setCurrentBSMonth(month);

          // Fetch the BS month data
          await fetchMonthData(year, month);

          // Find the day in calendar data
          const data = getDataForKey(year, month);
          if (data) {
            const dayInMonth = data.days.find(
              (d) => d.bs?.date === bsDateString
            );
            if (dayInMonth) {
              setSelectedBSKey(bsDateString);
              setSelectedADKey(dayInMonth.ad.date);
              setSelectedDate(bsDateString);
              // Set primary AD month when inputting a BS date (convert to AD)
              if (dayInMonth.ad) {
                setPrimaryADMonth(dayInMonth.ad.ad_m);
                setPrimaryADYear(dayInMonth.ad.ad_y);
              }
              onDateSelect?.(dayInMonth);
              return dayInMonth;
            } else {
              // Day not found in month, but we still navigated to the correct month
              // Set selected date anyway so input shows the correct value
              setSelectedBSKey(bsDateString);
              setSelectedDate(bsDateString);
              // Try to find the AD equivalent
              const adDate = bsToAD(year, month, day);
              if (adDate) {
                const adDateString = `${adDate.ad_y}-${String(
                  adDate.ad_m
                ).padStart(2, "0")}-${String(adDate.ad_d).padStart(2, "0")}`;
                setSelectedADKey(adDateString);
                setPrimaryADMonth(adDate.ad_m);
                setPrimaryADYear(adDate.ad_y);
              }
            }
          } else {
            // Data not loaded yet, but we navigated to the month
            // Set selected date so input shows correct value
            setSelectedBSKey(bsDateString);
            setSelectedDate(bsDateString);
            // Try to convert to AD
            const adDate = bsToAD(year, month, day);
            if (adDate) {
              const adDateString = `${adDate.ad_y}-${String(
                adDate.ad_m
              ).padStart(2, "0")}-${String(adDate.ad_d).padStart(2, "0")}`;
              setSelectedADKey(adDateString);
              setPrimaryADMonth(adDate.ad_m);
              setPrimaryADYear(adDate.ad_y);
            }
          }
        } else {
          // Validate AD date
          if (year < 1921 || year > 2032) {
            console.error("AD year out of range");
            return null;
          }
          if (month < 1 || month > 12) {
            console.error("Invalid AD month");
            return null;
          }

          const adDateString = `${year}-${String(month).padStart(
            2,
            "0"
          )}-${String(day).padStart(2, "0")}`;

          // Convert AD to BS
          const bsDate = adToBS(year, month, day);
          if (!bsDate) {
            console.error("Failed to convert AD to BS");
            return null;
          }

          // IMPORTANT: Set primary AD month FIRST so display updates immediately
          setPrimaryADMonth(month);
          setPrimaryADYear(year);

          // IMPORTANT: Set current BS year/month FIRST so calendar navigates immediately
          setCurrentBSYear(bsDate.bs_y);
          setCurrentBSMonth(bsDate.bs_m);

          // Fetch the BS month data
          await fetchMonthData(bsDate.bs_y, bsDate.bs_m);

          // Find the day in calendar data
          const data = getDataForKey(bsDate.bs_y, bsDate.bs_m);
          if (data) {
            const dayInMonth = data.days.find(
              (d) => d.ad?.date === adDateString
            );
            if (dayInMonth) {
              const bsDateString = `${bsDate.bs_y}-${String(
                bsDate.bs_m
              ).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
              setSelectedBSKey(bsDateString);
              setSelectedADKey(adDateString);
              setSelectedDate(adDateString);
              // The display will show correct month because primaryADMonth is set
              onDateSelect?.(dayInMonth);
              return dayInMonth;
            } else {
              // Day not found in month, but we still navigated to the correct month
              // Set selected date using converted BS date
              const bsDateString = `${bsDate.bs_y}-${String(
                bsDate.bs_m
              ).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
              setSelectedBSKey(bsDateString);
              setSelectedADKey(adDateString);
              setSelectedDate(adDateString);
              // The display will show correct month because primaryADMonth is set
            }
          } else {
            // Data not loaded yet, but we navigated to the month
            // Set selected date using converted BS date
            const bsDateString = `${bsDate.bs_y}-${String(
              bsDate.bs_m
            ).padStart(2, "0")}-${String(bsDate.bs_d).padStart(2, "0")}`;
            setSelectedBSKey(bsDateString);
            setSelectedADKey(adDateString);
            setSelectedDate(adDateString);
            // The display will show correct month because primaryADMonth is set
          }
        }
      } catch (error) {
        console.error("Error handling input date:", error);
      } finally {
        setIsInputLoading(false);
      }
      return null;
    },
    [
      fetchMonthData,
      getDataForKey,
      onDateSelect,
      setCurrentBSYear,
      setCurrentBSMonth,
      setSelectedBSKey,
      setSelectedADKey,
      setSelectedDate,
      setPrimaryADMonth,
      setPrimaryADYear,
      bsToAD,
      adToBS,
    ]
  );

  // Navigate to value prop's month when popup opens

  // Sync with prop only on mount or if prop changes and user hasn't manually toggled
  useEffect(() => {
    // Only sync if user hasn't manually toggled the mode
    if (!hasUserToggledMode.current) {
      // Only update if different to avoid unnecessary re-renders
      if (initialCalendarMode !== calendarMode) {
        setCalendarMode(initialCalendarMode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCalendarMode]); // Only sync when prop changes, not when internal state changes

  // Navigate to value prop's month when popup opens
  const navigateToValuePropMonth = useCallback(async () => {
    if (!value || !isIndexedDBInitialized) return;
    
    const dateParts = value.split("-");
    if (dateParts.length !== 3) return;
    
    const adYear = parseInt(dateParts[0], 10);
    const adMonth = parseInt(dateParts[1], 10);
    const adDay = parseInt(dateParts[2], 10);
    
    if (isNaN(adYear) || isNaN(adMonth) || isNaN(adDay)) return;
    if (adYear < 1921 || adYear > 2032) return;
    
    const bsDate = adToBS(adYear, adMonth, adDay);
    if (!bsDate) return;
    
    // Only navigate if we're not already on the correct month
    if (currentBSYear !== bsDate.bs_y || currentBSMonth !== bsDate.bs_m) {
      setCurrentBSYear(bsDate.bs_y);
      setCurrentBSMonth(bsDate.bs_m);
      await fetchMonthData(bsDate.bs_y, bsDate.bs_m);
      onMonthChange?.(bsDate.bs_y, bsDate.bs_m);
    }
    
    // Ensure primary AD month is set for AD mode toggle
    setPrimaryADMonth(adMonth);
    setPrimaryADYear(adYear);
  }, [value, isIndexedDBInitialized, currentBSYear, currentBSMonth, fetchMonthData, onMonthChange]);

  return {
    // State
    currentBSYear,
    currentBSMonth,
    calendarData,
    isLoading,
    isYearLoading,
    isMonthLoading,
    isInputLoading,
    calendarMode,
    isIndexedDBInitialized,
    showMonthDropdown,
    showYearDropdown,
    selectedDate,
    selectedBSKey,
    selectedADKey,
    primaryADMonth,
    primaryADYear,

    // Refs
    titleRef,

    // Setters
    setShowMonthDropdown,
    setShowYearDropdown,
    setSelectedDate,
    setSelectedBSKey,
    setSelectedADKey,

    // Helpers
    getCurrentKey,
    getDataForKey,
    getADMonthRange,
    getDisplayMonthYear,
    getDropdownDisplayValues,

    // Handlers
    handleDayClick,
    handlePrevMonth,
    handleNextMonth,
    handleCalendarModeChange,
    handleMonthSelect,
    handleYearSelect,
    handleInputDate,
    fetchMonthData,
    fetchYearData,
    fetchAllBSMonthsForADMonth,
    navigateToValuePropMonth,
  };
};

// Constants that need to be available
const months = [
  "बैशाख",
  "जेठ",
  "असार",
  "श्रावण",
  "भदौ",
  "आश्विन",
  "कार्तिक",
  "मंसिर",
  "पुष",
  "माघ",
  "फागुन",
  "चैत्र",
];

const monthsEn = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const toNepaliNumber = (num: number): string => {
  const nepaliDigits = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];
  return num
    .toString()
    .replace(/[0-9]/g, (match) => nepaliDigits[parseInt(match)]);
};

export { months, monthsEn, toNepaliNumber };
